import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { AgentView } from "@/components/AgentView";
import { CountUp } from "@/components/CountUp";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { eur, formatDate } from "@/lib/format";
import { LiveBadge } from "@/components/LiveBadge";
import { motion } from "framer-motion";
import { Users, Percent, Wallet, Crown } from "lucide-react";

function TeamTab() {
  const { user } = useAuth();
  const [agents, setAgents] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    const fetchAgents = () => api.get("/manager/agents").then((r) => setAgents(r.data)).catch(() => setAgents((p) => p || []));
    fetchAgents();
    const id = setInterval(fetchAgents, 6000);
    return () => clearInterval(id);
  }, []);

  const openDetail = (id) => {
    setDetail(id); setDetailData(null);
    api.get(`/manager/agents/${id}/sales`).then((r) => setDetailData(r.data));
  };

  if (!agents) return <Skeleton className="h-64 bg-[#FFFFFF]" />;

  const teamValue = agents.reduce((s, a) => s + a.prize_value, 0);
  const pct = user?.commission_pct || 0;
  const earning = teamValue * pct / 100;

  return (
    <div className="space-y-8" data-testid="team-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Metric icon={Percent} label="La mia commissione"><CountUp value={pct} suffix=" %" className="text-[#0F1E33]" /></Metric>
        <Metric icon={Wallet} label="Premi team sbloccati"><CountUp value={teamValue} prefix="€ " decimals={2} className="text-[#0F1E33]" /></Metric>
        <Metric icon={Crown} label="Guadagno maturato" highlight testid="manager-earning"><CountUp value={earning} prefix="€ " decimals={2} className="text-[#0B6BCB]" /></Metric>
      </div>

      <Card className="bg-[#FFFFFF] border-[#DCE7F5] overflow-hidden">
        <div className="p-5 border-b border-[#DCE7F5] font-display font-semibold flex items-center justify-between">
          <span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} /> Classifica del team</span>
          <LiveBadge />
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-[#DCE7F5] hover:bg-transparent">
              <TableHead className="text-[#5A6B85]">#</TableHead>
              <TableHead className="text-[#5A6B85]">Agente</TableHead>
              <TableHead className="text-[#5A6B85] text-center">Premi vinti</TableHead>
              <TableHead className="text-[#5A6B85] text-right">Vendite</TableHead>
              <TableHead className="text-[#5A6B85] text-right">Premio maturato</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agents.map((a, i) => (
              <motion.tr
                layout
                key={a.id}
                onClick={() => openDetail(a.id)}
                data-testid={`team-row-${a.id}`}
                className="border-b border-[#DCE7F5] cursor-pointer hover:bg-[#EEF4FC] transition-colors"
                transition={{ layout: { type: "spring", stiffness: 200, damping: 26 } }}
              >
                <TableCell><span className={i === 0 ? "text-[#0B6BCB] font-bold" : "text-[#5A6B85]"}>{i + 1}</span></TableCell>
                <TableCell className="font-medium">{a.full_name}</TableCell>
                <TableCell className="text-center text-[#5A6B85]">{a.prizes_won}</TableCell>
                <TableCell className="text-right text-[#5A6B85]">{a.count}</TableCell>
                <TableCell className="text-right font-semibold text-[#0B6BCB]">{eur(a.prize_value)}</TableCell>
              </motion.tr>
            ))}
            {agents.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#5A6B85] py-8">Nessun agente nel team</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33] max-w-2xl" data-testid="agent-detail-dialog">
          <DialogHeader><DialogTitle className="font-display">Vendite di {detailData?.agent?.full_name || "..."}</DialogTitle></DialogHeader>
          {!detailData ? <Skeleton className="h-40 bg-[#EEF4FC]" /> : detailData.breakdown.length === 0 ? (
            <p className="text-sm text-[#5A6B85] py-6 text-center">Nessuna promozione nella campagna attiva.</p>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-auto pr-1" data-testid="team-agent-sales-breakdown">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#5A6B85]">Totale premio maturato</span>
                <span className="font-display font-bold text-[#0B6BCB]">{eur(detailData.total_prize_value)}</span>
              </div>
              {detailData.breakdown.map((b) => (
                <div key={b.promotion_id} className="border border-[#DCE7F5] rounded-lg overflow-hidden">
                  <div className="p-3 bg-[#F5F9FF] flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate">{b.promotion_name}</span>
                      <Badge className="bg-[#EEF4FC] text-[#0B6BCB] border-[#DCE7F5] text-[10px] uppercase">{b.type === "pezzi" ? "Pezzi" : "Vendite"}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-sm shrink-0">
                      <span className="text-[#5A6B85]">{b.count} / {b.target}</span>
                      <span className={b.achieved ? "font-semibold text-[#0B6BCB]" : "text-[#94A3B8]"}>{eur(b.value)}</span>
                    </div>
                  </div>
                  {b.sales.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-[#94A3B8]">Nessuna vendita registrata</div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                          <TableHead className="text-[#5A6B85]">Cliente</TableHead>
                          {b.type === "pezzi" && <TableHead className="text-[#5A6B85] text-right">Pezzi</TableHead>}
                          <TableHead className="text-[#5A6B85]">Note</TableHead>
                          <TableHead className="text-[#5A6B85] text-right">Data</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {b.sales.map((s) => (
                          <TableRow key={s.id} className="border-[#DCE7F5]" data-testid={`team-agent-sale-${s.id}`}>
                            <TableCell className="font-medium">{s.client_name || "-"}</TableCell>
                            {b.type === "pezzi" && <TableCell className="text-right text-[#0B6BCB] font-semibold">{s.quantity || 1}</TableCell>}
                            <TableCell className="text-[#5A6B85] text-sm">{s.note || "-"}</TableCell>
                            <TableCell className="text-right text-[#5A6B85] text-sm">{formatDate(s.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ icon: Icon, label, children, highlight, testid }) {
  return (
    <Card className={`p-6 bg-[#FFFFFF] ${highlight ? "border-[#0B6BCB]/30" : "border-[#DCE7F5]"}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[#5A6B85] mb-3">
        <Icon className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-wide">{label}</span>
      </div>
      <div className="font-display font-bold text-3xl tracking-tight">{children}</div>
    </Card>
  );
}

export default function ManagerDashboard() {
  return (
    <Layout>
      <Tabs defaultValue="dashboard" className="space-y-6">
        <TabsList className="bg-[#FFFFFF] border border-[#DCE7F5] h-auto p-1">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#0B6BCB] data-[state=active]:text-[#FFFFFF] px-4 py-2" data-testid="tab-dashboard">I miei progressi</TabsTrigger>
          <TabsTrigger value="team" className="data-[state=active]:bg-[#0B6BCB] data-[state=active]:text-[#FFFFFF] px-4 py-2" data-testid="tab-team">Team</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard">
          <AgentView title="I miei progressi" subtitle="Come capo area partecipi anche come agente." />
        </TabsContent>
        <TabsContent value="team"><TeamTab /></TabsContent>
      </Tabs>
    </Layout>
  );
}
